import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGetAssistantSpeechTokenMutation } from "../../graphql/generated/graphql";
import { ChatMessage } from "./useAssistantRealtime";

type TranslateFn = (key: string, defaultValue?: string) => string;

type UseAssistantSpeechRecognitionOptions = {
  open: boolean;
  i18nLanguage?: string;
  translate: TranslateFn;
  addMessage: (message: ChatMessage) => void;
  messageText: string;
  setMessageText: (value: string) => void;
  focusInput: () => void;
  isControlDisabled: boolean;
  silenceAutoStopMs?: number;
};

export default function useAssistantSpeechRecognition(options: UseAssistantSpeechRecognitionOptions)
{
  const {
    open,
    i18nLanguage,
    translate,
    addMessage,
    messageText,
    setMessageText,
    focusInput,
    isControlDisabled,
    silenceAutoStopMs = 3000,
  } = options;

  const [isSpeechStarting, setIsSpeechStarting] = useState(false);
  const [isSpeechRecognizing, setIsSpeechRecognizing] = useState(false);
  const speechRecognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null);
  const speechStartCanceledRef = useRef(false);
  const speechStopRequestedRef = useRef(false);
  const speechFinalTextRef = useRef<string>('');
  const speechInterimTextRef = useRef<string>('');
  const speechSilenceTimeoutRef = useRef<number | null>(null);

  const [getAssistantSpeechTokenMutation] = useGetAssistantSpeechTokenMutation();

  const isSpeechRecognitionSupported = typeof window !== 'undefined' && typeof navigator !== 'undefined';
  const isSpeechActive = isSpeechRecognizing || isSpeechStarting;
  const isInputLocked = isSpeechActive;

  const buildSpeechText = useCallback((finalText: string, interimText: string): string =>
  {
    const finalized = finalText.trim();
    const interim = interimText.trim();
    if(finalized && interim) return `${finalized} ${interim}`;
    return finalized || interim;
  }, []);

  const applySpeechText = useCallback(() =>
  {
    setMessageText(buildSpeechText(speechFinalTextRef.current, speechInterimTextRef.current));
  }, [buildSpeechText, setMessageText]);

  const logSpeech = useCallback((message: string, details?: Record<string, unknown>) =>
  {
    if(details)
    {
      console.info(`[AssistantSpeech] ${message}`, details);
      return;
    }

    console.info(`[AssistantSpeech] ${message}`);
  }, []);

  const stopSpeechRecognition = useCallback(async () =>
  {
    logSpeech('Stop requested', {
      hasRecognizer: Boolean(speechRecognizerRef.current),
    });

    if(speechSilenceTimeoutRef.current !== null)
    {
      window.clearTimeout(speechSilenceTimeoutRef.current);
      speechSilenceTimeoutRef.current = null;
      logSpeech('Silence auto-stop timer cleared');
    }

    speechStopRequestedRef.current = true;
    speechStartCanceledRef.current = true;
    const recognizer = speechRecognizerRef.current;
    speechRecognizerRef.current = null;
    setIsSpeechStarting(false);
    setIsSpeechRecognizing(false);
    speechInterimTextRef.current = '';

    if(!recognizer)
    {
      logSpeech('Stop requested without active recognizer');
      applySpeechText();
      return;
    }

    recognizer.recognizing = () => undefined;
    recognizer.recognized = () => undefined;
    recognizer.sessionStopped = () => undefined;
    recognizer.canceled = () => undefined;

    await new Promise<void>(resolve =>
    {
      recognizer.stopContinuousRecognitionAsync(() => resolve(), () => resolve());
    });

    logSpeech('Recognizer stopped, closing instance');
    recognizer.close();
    applySpeechText();
    setTimeout(() => focusInput(), 0);
  }, [applySpeechText, focusInput, logSpeech]);

  const stopSpeechRecognitionRef = useRef(stopSpeechRecognition);

  useEffect(() =>
  {
    stopSpeechRecognitionRef.current = stopSpeechRecognition;
  }, [stopSpeechRecognition]);

  const toggleSpeechRecognition = useCallback(async () =>
  {
    logSpeech('Toggle requested', {
      isActive: isSpeechActive,
      isSupported: isSpeechRecognitionSupported,
      isDisabled: isControlDisabled,
    });

    if(isSpeechActive)
    {
      await stopSpeechRecognition();
      return;
    }

    if(!isSpeechRecognitionSupported || isControlDisabled) return;

    speechStopRequestedRef.current = false;
    speechStartCanceledRef.current = false;
    setIsSpeechStarting(true);

    try
    {
      if(!navigator.mediaDevices?.getUserMedia)
      {
        throw new Error('A böngésző nem támogatja a mikrofon hozzáférést (mediaDevices.getUserMedia).');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());

      const speechTokenResult = await getAssistantSpeechTokenMutation();
      const speechTokenErrors = (speechTokenResult.errors ?? []).map(error => error.message).filter(Boolean);
      if(speechTokenErrors.length > 0)
      {
        throw new Error(speechTokenErrors.join(' | '));
      }
      const speechTokenData = speechTokenResult.data?.GetAssistantSpeechToken;
      const token = speechTokenData?.Token?.trim();
      const region = speechTokenData?.Region?.trim();
      const endpoint = speechTokenData?.Endpoint?.trim();
      if(!token) throw new Error('Speech token is missing.');

      logSpeech('Speech token received', {
        hasToken: Boolean(token),
        tokenLength: token.length,
        region,
        endpoint,
      });

      if(speechStartCanceledRef.current) return;

      speechFinalTextRef.current = messageText?.toString().trim() ?? '';
      speechInterimTextRef.current = '';

      if(!region && !endpoint)
      {
        throw new Error('Speech region or endpoint is required.');
      }

      let speechConfig: SpeechSDK.SpeechConfig;
      if(region)
      {
        speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
        logSpeech('Speech config created via authorization token + region', { region });
      } else
      {
        speechConfig = SpeechSDK.SpeechConfig.fromEndpoint(new URL(endpoint!));
        speechConfig.authorizationToken = token;
        logSpeech('Speech config created via endpoint + authorization token', { endpoint });
      }

      speechConfig.speechRecognitionLanguage = (i18nLanguage ?? 'en').toLowerCase().startsWith('hu')
        ? 'hu-HU'
        : 'en-US';
      logSpeech('Speech recognition language configured', {
        i18nLanguage,
        recognitionLanguage: speechConfig.speechRecognitionLanguage,
      });

      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      logSpeech('Speech recognizer created');

      const clearSilenceAutoStop = () =>
      {
        if(speechSilenceTimeoutRef.current === null) return;

        window.clearTimeout(speechSilenceTimeoutRef.current);
        speechSilenceTimeoutRef.current = null;
        logSpeech('Silence auto-stop timer cleared');
      };

      const scheduleSilenceAutoStop = (source: string) =>
      {
        clearSilenceAutoStop();

        speechSilenceTimeoutRef.current = window.setTimeout(() =>
        {
          if(speechStopRequestedRef.current) return;

          logSpeech('Auto stop after silence', {
            source,
            timeoutMs: silenceAutoStopMs,
          });
          void stopSpeechRecognition();
        }, silenceAutoStopMs);

        logSpeech('Silence auto-stop timer armed', {
          source,
          timeoutMs: silenceAutoStopMs,
        });
      };

      recognizer.sessionStarted = (_, event) =>
      {
        logSpeech('Session started', { sessionId: event.sessionId });
      };

      recognizer.speechStartDetected = (_, event) =>
      {
        logSpeech('Speech start detected', { sessionId: event.sessionId });
        clearSilenceAutoStop();
      };

      recognizer.speechEndDetected = (_, event) =>
      {
        logSpeech('Speech end detected', { sessionId: event.sessionId });
        scheduleSilenceAutoStop('speech-end-detected');
      };

      recognizer.recognizing = (_, event) =>
      {
        if(event.result.reason !== SpeechSDK.ResultReason.RecognizingSpeech) return;
        logSpeech('Recognizing (interim)', { text: event.result.text });
        clearSilenceAutoStop();
        speechInterimTextRef.current = event.result.text ?? '';
        applySpeechText();
      };

      recognizer.recognized = (_, event) =>
      {
        logSpeech('Recognized event', {
          reason: event.result.reason,
          text: event.result.text,
        });

        if(event.result.reason === SpeechSDK.ResultReason.NoMatch)
        {
          scheduleSilenceAutoStop('recognized-no-match');
          addMessage({
            id: `speech-no-match-${Date.now()}`,
            role: 'system',
            content: translate('partnerPortal.assistant.speechNoMatch', 'Nem sikerült értelmezhető beszédet felismerni. Próbáld meg közelebb a mikrofonhoz beszélve.'),
            timestamp: new Date().toISOString()
          });
          return;
        }

        if(event.result.reason !== SpeechSDK.ResultReason.RecognizedSpeech) return;

        const recognizedText = (event.result.text ?? '').trim();
        if(!recognizedText) return;

        speechFinalTextRef.current = buildSpeechText(speechFinalTextRef.current, recognizedText);
        speechInterimTextRef.current = '';
        applySpeechText();
        scheduleSilenceAutoStop('recognized-speech');
      };

      recognizer.sessionStopped = () =>
      {
        logSpeech('Session stopped');
        clearSilenceAutoStop();
        void stopSpeechRecognition();
      };

      recognizer.canceled = (_, event) =>
      {
        logSpeech('Recognition canceled', {
          reason: event.reason,
          errorCode: event.errorCode,
          errorDetails: event.errorDetails,
        });
        clearSilenceAutoStop();

        if(!speechStopRequestedRef.current && event.reason === SpeechSDK.CancellationReason.Error)
        {
          addMessage({
            id: `speech-cancel-error-${Date.now()}`,
            role: 'system',
            content: `${translate('partnerPortal.assistant.speechCanceledWithError')} ${event.errorDetails || event.errorCode || 'ismeretlen hiba'}`,
            timestamp: new Date().toISOString()
          });
        }
        void stopSpeechRecognition();
      };

      speechRecognizerRef.current = recognizer;

      await new Promise<void>((resolve, reject) =>
      {
        recognizer.startContinuousRecognitionAsync(
          () =>
          {
            logSpeech('Continuous recognition started');
            scheduleSilenceAutoStop('recognition-started');
            resolve();
          },
          error => reject(new Error(typeof error === 'string' ? error : 'Unable to start speech recognition.'))
        );
      });

      if(speechStartCanceledRef.current)
      {
        await stopSpeechRecognition();
        return;
      }

      setIsSpeechRecognizing(true);
    } catch(error)
    {
      console.error('[AssistantSpeech] Error starting speech recognition:', error);
      addMessage({
        id: `speech-error-${Date.now()}`,
        role: 'system',
        content: `${translate('partnerPortal.assistant.speechStartFailed')} ${(error as any)?.message ?? 'ismeretlen hiba'}`,
        timestamp: new Date().toISOString()
      });
      await stopSpeechRecognition();
    } finally
    {
      setIsSpeechStarting(false);
    }
  }, [addMessage, applySpeechText, buildSpeechText, getAssistantSpeechTokenMutation, i18nLanguage, isControlDisabled, isSpeechActive, isSpeechRecognitionSupported, logSpeech, messageText, silenceAutoStopMs, stopSpeechRecognition, translate]);

  useEffect(() =>
  {
    if(open) return;
    if(!isSpeechActive) return;
    void stopSpeechRecognition();
  }, [isSpeechActive, open, stopSpeechRecognition]);

  useEffect(() => () =>
  {
    void stopSpeechRecognitionRef.current();
  }, []);

  return {
    isSpeechRecognitionSupported,
    isSpeechActive,
    isInputLocked,
    toggleSpeechRecognition,
  };
}
